import { useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { AppsContainer } from "@lib/components/AppComponents";
import { useCustomerLeadQueries } from "@lib/apiQueries/useCustomerLeadQueries";
import { AppContext, SharedContextProps } from "@lib/types/Context";
import { ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";
import { getCustomerLeadDetailTabs } from "@app/shared/customerLeadDetail/Tabs";
import { CustomerLogo } from "@lib/components/MiniComponents/Others";
import {
  SidebarCustomerLeadDetailListItems,
  SidebarSendByteWalletAddressSummaryListItems,
} from "./SidebarUtils";
import { useQuery } from "@tanstack/react-query";
import { apiGetSendByteWalletAddress } from "@lib/api/walletSendByte";
import CustomerLeadModal from "@lib/components/PageComponents/Customer/CustomerLeadModal";
import { useTypedSelector } from "@lib/redux";
import { AuthRoles } from "@lib/types";

const coverImg = "/assets/images/wall/v-card.jpg";

const SharedCustomerLeadDetail: React.FC<SharedContextProps> = ({
  context = AppContext.CUSTOMER_LEAD,
  customTitle,
}) => {
  const { messages } = useIntl();
  const { role } = useTypedSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const { customerLeadId, tab } = useParams();
  const Tabs = getCustomerLeadDetailTabs();

  const route =
    role === AuthRoles.Admin
      ? ROUTES.ADMIN.CUSTOMER_LEAD
      : ROUTES.CUSTOMER_LEAD;

  const { customerLeadQuery } = useCustomerLeadQueries({
    id: customerLeadId,
    messages,
    queryKey: ["customer-lead", customerLeadId],
    params: {
      join: ["customer"],
    },
  });

  const { data, isLoading, isFetching } = customerLeadQuery;
  const customerId = data?.data?.customerId;

  useEffect(() => {
    if (!customerLeadId) {
      toast.error("customer lead ID is undefined");
      navigate(route.LIST);
    }
  }, [customerLeadId]);

  useEffect(() => {
    if (!isLoading && !isFetching && !data) {
      navigate(route.LIST);
    }
  }, [isLoading, isFetching, data]);

  const [selectedTab, setSelectedTab] = useState<TabKeys>(tab || Tabs[0].key);
  const _keys = Tabs.map((tab) => tab.key);
  type TabKeys = (typeof _keys)[number];

  useEffect(() => {
    handleTabClick(tab || Tabs[0].key);
  }, [tab]);

  const handleTabClick = (key: TabKeys) => {
    setSelectedTab(key);
    navigate(
      getRoute(route.DETAIL_TAB, {
        customerLeadId: customerLeadId,
        tab: key,
      }),
    );
  };
  const walletAddressQuery = useQuery({
    queryFn: () =>
      apiGetSendByteWalletAddress({
        filter: [`customerUserId||$eq||${data?.data?.id}`],
        sort: "createdAt,DESC",
      }),
    queryKey: ["customer-lead-wallet-address", data?.data?.id],
    enabled: !!data?.data?.id && !isLoading,
  });
  const { data: walletAddressData, isLoading: walletAddressLoading } =
    walletAddressQuery;
  const walletAddressApiData = walletAddressData?.data?.data;

  const [openModal, setModalOpen] = useState(false);
  const { updateCustomerLeadMutation } = useCustomerLeadQueries({
    messages,
  });

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const title = customTitle || (messages["customer.lead.detail"] as string);

  return (
    <AppsContainer
      isLoading={isLoading || isFetching}
      title={title}
      breadcrumbProps={{
        urlParams: {
          customerLeadId: `${data?.data?.firstName} ${data?.data?.lastName}`,
        },
      }}
      sidebarContent={
        <AppsContainer.Sidebar>
          <AppsContainer.SidebarUserCard
            cover={<img src={coverImg} alt="" />}
            avatar={
              <CustomerLogo customer={data?.data?.customer} size="large" />
            }
            title={`${data?.data?.firstName} ${data?.data?.lastName}`}
            editAction={() => toggle()}
            listItems={SidebarCustomerLeadDetailListItems({
              customerLead: data?.data,
              isLoading: isLoading || isFetching,
            })}
            loading={isLoading || isFetching}
          />
          <AppsContainer.SidebarUserCard
            header={{
              title: messages["customer.tabs.walletAccountsSendByte"] as string,
              extra: (
                <Link
                  to={getRoute(route.DETAIL_TAB, {
                    customerLeadId: customerLeadId,
                    tab: "walletAccounts",
                  })}
                >
                  {messages["common.listAll"] as string}
                </Link>
              ),
            }}
            listItems={SidebarSendByteWalletAddressSummaryListItems({
              sendByteWalletAddressData: walletAddressApiData,
              isLoading: isLoading || walletAddressLoading,
            })}
            loading={isLoading || walletAddressLoading}
          />
        </AppsContainer.Sidebar>
      }
      sidebarProviderProps={{ doNotTrack: true }}
      tabs={{
        tabs: Tabs.map((tab) => ({
          ...tab,
          isActive: selectedTab === tab.key,
          onClick: () => handleTabClick(tab.key),
          loading: isLoading || isFetching,
          props: {
            leadId: customerLeadId,
            customerLead: data?.data,
            customerId: customerId,
            pageIsActive: selectedTab === tab.key,
            withoutContainer: true,
            context: context,
            detailContext: context,
            minusHeight: 50,
          },
        })),
        selectedTab: selectedTab,
        onChange: (key: string) => handleTabClick(key as TabKeys),
      }}
    >
      <CustomerLeadModal
        mutationQuery={updateCustomerLeadMutation}
        openModal={openModal}
        toggleModal={toggle}
        customerLead={data?.data}
      />
    </AppsContainer>
  );
};

export default SharedCustomerLeadDetail;
